import { useMemo } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import {
  capitalize,
  isBoolean,
  isNull,
  isObject,
  isString,
  times,
} from "lodash-es";

import { GenericObject } from "src/api/flowTypes";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TableComp } from "src/base-components/Table";
import { FeatureType } from "src/clients/features-control";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ColumnTypeSelector } from "src/featureCatalogue/Queries/Create/ColumnTypeSelector";
import { ColumnType as ItemColumnType } from "src/featureCatalogue/Queries/Create/helpers";
import { JobPreviewResult } from "src/jobs/api/queries";
import { TYPE_ICONS } from "src/utils/constants";
import { formatDate, isISO8601 } from "src/utils/datetime";
import {
  objectToSpeakPythonString,
  speakPythonPrimitive,
} from "src/utils/speakPython";

export const SQLPreviewTable: React.FC<{
  rows?: JobPreviewResult["data"]["result"];
  isLoading?: boolean;
  dataLoc?: string;
  columnTypes?: Record<string, ItemColumnType>;
  onColumnTypeChange?: (column: string, type: ItemColumnType) => void;
}> = ({
  rows = [],
  isLoading = false,
  dataLoc = "sql-preview-table",
  columnTypes = {},
  onColumnTypeChange,
}) => {
  const columns = useMemo(
    () => getColumns(rows, isLoading, columnTypes, onColumnTypeChange),
    [rows, isLoading, columnTypes, onColumnTypeChange],
  );
  return (
    <div className="max-h-full min-h-0 max-w-full overflow-auto">
      <TableComp
        columns={columns}
        data={rows}
        dataLoc={dataLoc}
        frameClassName="w-full"
        isLoading={isLoading}
      />
    </div>
  );
};

const PreviewHeader: React.FC<{
  children: React.ReactNode;
  icon: React.ReactNode;
  columnName: string;
  currentColumnType: ItemColumnType;
  columnTypes: Record<string, ItemColumnType>;
  onColumnTypeChange?: (column: string, type: ItemColumnType) => void;
}> = ({
  children,
  icon,
  columnName,
  currentColumnType,
  columnTypes,
  onColumnTypeChange,
}) => {
  return (
    <div className="flex items-center justify-between gap-x-2 border-r border-r-gray-100 bg-gray-50 py-2 pr-2 pl-1 font-inter-medium-12px">
      <div className="flex items-center gap-x-0.5">
        {icon}
        <span>{children}</span>
      </div>
      <ColumnTypeSelector
        columnName={columnName}
        columnTypes={columnTypes}
        currentColumnType={currentColumnType}
        onColumnTypeChange={onColumnTypeChange}
      />
    </div>
  );
};

const columnHelper = createColumnHelper<GenericObject>();

const getColumns = (
  data: JobPreviewResult["data"]["result"],
  isLoading: boolean,
  columnTypes: Record<string, ItemColumnType>,
  onColumnTypeChange?: (column: string, type: ItemColumnType) => void,
) => {
  if (data.length) {
    const types = inferDataTypes(data);
    return Object.keys(data[0]).map((key) =>
      columnHelper.accessor(key, {
        header: ({ header }) => (
          <PreviewHeader
            columnName={header.id}
            columnTypes={columnTypes}
            currentColumnType={columnTypes[header.id] || "feature"}
            icon={
              <Tooltip
                placement="top"
                title={
                  types[header.id] ? capitalize(types[header.id]) : "Unknown"
                }
              >
                <Icon icon={TYPE_ICONS[types[header.id] ?? "any"]} size="2xs" />
              </Tooltip>
            }
            onColumnTypeChange={onColumnTypeChange}
          >
            {header.id}
          </PreviewHeader>
        ),
        cell: ({ cell }) => (
          <div className="truncate border-r border-r-gray-100 py-2 pr-2 pl-1.5 font-inter-normal-12px">
            {renderValue(cell.getValue(), types[cell.column.id] ?? "any")}
          </div>
        ),
      }),
    );
  }

  if (isLoading) {
    return times(5, (i) =>
      columnHelper.accessor(`loading_${i}`, {
        header: () => (
          <div className="flex items-center justify-between gap-x-2 border-r border-r-gray-100 bg-gray-50 py-2 pr-2 pl-1 font-inter-medium-12px">
            <div className="flex items-center gap-x-0.5">
              <SkeletonPlaceholder height="h-4" width="w-4" />
              <SkeletonPlaceholder height="h-4" width="w-16" />
            </div>
            <SkeletonPlaceholder height="h-5" width="w-20" />
          </div>
        ),
      }),
    );
  }

  return [];
};

const renderValue = (value: unknown, type: ColumnType): React.ReactNode => {
  if (isNull(value) || isBoolean(value))
    return speakPythonPrimitive(String(value));

  if (type === "datetime" && isString(value)) {
    return formatDate(value);
  }

  if ((type === "array" || type === "object") && isObject(value)) {
    return objectToSpeakPythonString(value);
  }

  return String(value);
};

const inferColumnDataType = (value: unknown): FeatureType | undefined => {
  if (typeof value === "number") {
    return FeatureType.NUMBER;
  } else if (typeof value === "boolean") {
    return FeatureType.BOOLEAN;
  } else if (typeof value === "string") {
    return !isISO8601(value) ? FeatureType.STRING : FeatureType.DATETIME;
  } else if (Array.isArray(value)) {
    return FeatureType.ARRAY;
  } else if (typeof value === "object") {
    return FeatureType.OBJECT;
  } else {
    return undefined;
  }
};

export const inferDataTypes = (rows: JobPreviewResult["data"]["result"]) => {
  const types: Record<string, FeatureType | undefined> = {};
  const fieldsCount = Object.keys(rows.at(0) ?? {}).length;

  for (const row of rows) {
    for (const key in row) {
      if (row[key] !== null && !types[key]) {
        types[key] = inferColumnDataType(row[key]);
      }

      if (fieldsCount === Object.keys(types).length) {
        // Quit early if all types are detected
        return types;
      }
    }
  }

  return types;
};

type ColumnType = keyof typeof TYPE_ICONS;
